import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StandardModal, { ModalSize } from '../../Fundamentals/Modal/StandardModal';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import Checkbox from '../../Fundamentals/Form/Checkbox';
import PackageStatusBadge from './PackageStatusBadge';
import { versionTransitionConfig } from './versionUtils';
import type { VossVersionStatus } from '../../../Constants/VOSS/vossVersionStatus';
import type { PackageDetail } from './Packages.types';

interface PackageVersionStatusModalProps {
    vossPackage: PackageDetail;
    versionLabel: string;
    targetStatus: VossVersionStatus;
    replacedVersionLabel?: string;
    isSaving: boolean;
    onConfirm: () => void;
}

const PackageVersionStatusModal = ({ vossPackage, versionLabel, targetStatus, replacedVersionLabel, isSaving, onConfirm }: PackageVersionStatusModalProps) => {
    const { t } = useTranslation();
    const [acknowledged, setAcknowledged] = useState(false);

    const config = versionTransitionConfig[targetStatus];
    const comment = vossPackage.comment ?? '';
    const replacesPublished = replacedVersionLabel !== undefined && replacedVersionLabel.length > 0;

    return (
        <StandardModal
            title={t(config.titleKey, { version: versionLabel })}
            size={ModalSize.SMALL}
            isLoading={isSaving}
            submitLabel={t(config.confirmKey) ?? ''}
            cancelLabel={t('common.cancel') ?? ''}
            submitDisabled={config.requiresAcknowledgement && !acknowledged}
            onSubmit={onConfirm}
        >
            <div className="d-flex align-items-center gap-8 mb-16">
                <PackageStatusBadge status={vossPackage.status} />
                <SpirisIcon name="arrow-right" size="sm" />
                <PackageStatusBadge status={targetStatus} />
            </div>
            <p>{t(config.textKey)}</p>
            {config.requiresAcknowledgement && (
                <>
                    <div className="alert alert-warning alert-sm mw-100 min-h-0 mb-16" role="alert">
                        {replacesPublished ? t('packages.publishReplaces', { current: replacedVersionLabel }) : t('packages.publishFirst')}
                    </div>
                    <p className="mb-16">
                        <span className="font-weight-600">{t('packages.versionComment')}:</span>{' '}
                        {comment.length > 0 ? comment : <span className="text-secondary">{t('packages.noVersionComment')}</span>}
                    </p>
                    <Checkbox id="publish-acknowledge" label={t('packages.publishAcknowledge')} checked={acknowledged} onChange={setAcknowledged} />
                </>
            )}
        </StandardModal>
    );
};

export default PackageVersionStatusModal;
